import { defaultBreakpoints, defaultWeights } from '../opportunity_calculator/indicatorConfig';
import { DEFAULT_TIER_THRESHOLDS } from './riskTiers';

// Risk Calculator query params are r-prefixed so they never collide with the
// Opportunity Calculator's (w, bp, min, ...) if both ever share a URL.

const nearlyEqual = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 1e-9;

export function parseRiskSearch(searchParams, indicators, geographies) {
  const weights = defaultWeights(indicators);
  const encodedWeights = searchParams.get('rw');
  if (encodedWeights) {
    const parts = encodedWeights.split(',');
    if (parts.length === indicators.length) {
      indicators.forEach((indicator, index) => {
        const parsed = Number(parts[index]);
        weights[indicator.id] = Number.isFinite(parsed) ? parsed : indicator.defaultWeight;
      });
    }
  }

  const breakpointsById = defaultBreakpoints(indicators);
  (searchParams.get('rbp') || '').split(';').forEach((chunk) => {
    const [id, rest] = chunk.split(':');
    if (!id || !rest || !breakpointsById[id]) return;
    const values = rest.split('|').map(Number);
    if (values.length === 4 && values.every(Number.isFinite)) breakpointsById[id] = values;
  });

  const thresholds = { ...DEFAULT_TIER_THRESHOLDS };
  const tiers = (searchParams.get('rt') || '').split('|').map(Number);
  if (tiers.length === 3 && tiers.every(Number.isFinite)) {
    [thresholds.moderate, thresholds.high, thresholds.veryHigh] = tiers;
  }

  const [geoId, ...geoRest] = (searchParams.get('rgeo') || '').split(':');
  const geoLevel = (geographies || []).some((g) => g.id === geoId) ? geoId : 'state';
  const steady = searchParams.get('rsteady');
  const band = searchParams.get('rband');

  return {
    weights,
    breakpointsById,
    thresholds,
    geoLevel,
    geoValue: geoLevel === 'state' ? '' : geoRest.join(':'),
    steadyMode: ['steady', 'phasing'].includes(steady) ? steady : 'both',
    gradeBand: ['Elementary', 'Middle', 'High'].includes(band) ? band : 'all',
    renormalize: searchParams.get('rrenorm') !== '0',
  };
}

export function serializeRiskSearch({
  indicators,
  weights,
  breakpointsById,
  thresholds,
  geoLevel,
  geoValue,
  steadyMode,
  gradeBand,
  renormalize,
}) {
  const params = new URLSearchParams();
  const defaults = defaultWeights(indicators);
  if (indicators.some((i) => !nearlyEqual(weights[i.id], defaults[i.id]))) {
    params.set('rw', indicators.map((i) => String(weights[i.id] ?? 0)).join(','));
  }
  const defaultBp = defaultBreakpoints(indicators);
  const bpParts = indicators
    .filter((i) => (breakpointsById[i.id] || []).some((v, idx) => !nearlyEqual(v, defaultBp[i.id][idx])))
    .map((i) => `${i.id}:${breakpointsById[i.id].join('|')}`);
  if (bpParts.length) params.set('rbp', bpParts.join(';'));
  const t = DEFAULT_TIER_THRESHOLDS;
  if (thresholds.moderate !== t.moderate || thresholds.high !== t.high || thresholds.veryHigh !== t.veryHigh) {
    params.set('rt', [thresholds.moderate, thresholds.high, thresholds.veryHigh].join('|'));
  }
  if (geoLevel && geoLevel !== 'state' && geoValue) params.set('rgeo', `${geoLevel}:${geoValue}`);
  if (steadyMode && steadyMode !== 'both') params.set('rsteady', steadyMode);
  if (gradeBand && gradeBand !== 'all') params.set('rband', gradeBand);
  if (!renormalize) params.set('rrenorm', '0');
  return params;
}
